import { BaseEntity } from '../base/BaseEntity'
import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import type { WorkOrderStatusEnum } from './WorkOrderStatusEnum'
import { Product } from '../product/Product'
import { RelatedEntity } from '../base/RelatedEntity'
import { Order } from '../order/Order'
import { InventoryBySerialNumber } from '../inventorybyserialnumber/InventoryBySerialNumber'
import { Project } from '../project/Project'
import { WorkOrderItem } from './WorkOrderItem'
import { WorkOrderInsufficientItem } from './WorkOrderInsufficientItem'
import { Activity } from '../activity/Activity'
import { Logistic } from '../logistic/Logistic'
import { PurchaseOrder } from '../purchase-order/PurchaseOrder'
import { Task } from '../task/Task'
import { Asset } from '../asset/Asset'
import { generateEntityId } from '../generateEntityId'

@ObjectType()
@InputType('WorkOrderInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class WorkOrder extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('wo') })
  declare _id: string

  @Field(() => Date)
  @Prop({ type: Date })
  completionDate: Date

  @Field(() => String)
  @Prop({ type: String })
  declare status: WorkOrderStatusEnum

  @Field(() => [Order])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedSalesOrders: ReturnType<() => Order[]>

  @Field(() => [Logistic])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedLogistics: ReturnType<() => Logistic[]>

  /**
   * If this work order requires sub-workOrder to complete, this field should show the which are work orders related to this
   */
  @Field(() => [WorkOrder])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedWorkOrders: ReturnType<() => WorkOrder[]>

  /**
   * Purchase orders related to this work order (bidirectional link between procurement and production)
   */
  @Field(() => [PurchaseOrder])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedPurchaseOrders: ReturnType<() => PurchaseOrder[]>

  @Field(() => [WorkOrderItem])
  @Prop({ type: [WorkOrderItem] }, PropType.ARRAY)
  items: ReturnType<() => WorkOrderItem[]>

  /**
   * The product for this work order is being focus to
   */
  @Field(() => Product)
  @Prop({ type: RelatedEntity })
  product: ReturnType<() => Product>

  /**
   * The number of items to be produce for this product
   */
  @Field(() => Float)
  @Prop({ type: Number })
  quantity: number

  /**
   * Flag to determine if the workOrder order needs to be prioritized
   */
  @Field(() => Boolean)
  @Prop({ type: Boolean })
  prioritized: boolean

  /**
   * Flag to indicate if inventory has already been adjusted by a task status action hook.
   * Prevents duplicate inventory adjustments when tasks complete.
   */
  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean, default: false })
  inventoryAdjustedByTaskAction?: boolean

  /**
   * Denormalized flag set at creation time on child work orders.
   * Write-once: set to true when created via CreateFromWorkOrderItem or CreateAndBuildWorkOrder.
   */
  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean })
  hasParent?: boolean

  /**
   * Miscellaneous cost — raw materials and other miscellaneous expenses.
   * Editable inline on the Estimates/Cost tab.
   * Initial value: product.miscellaneousCost x quantity. Recalculated when WO quantity changes.
   */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number, default: 0 })
  miscellaneousCost: number

  /**
   * Sum of all task cost totals (labor and machine costs from production operations).
   * Recalculated when any task's laborCost, estimatedDuration, or perUnitCost changes.
   */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number, default: 0 })
  totalOperationsCost: number

  /**
   * Sum of insufficientItems[].cost (manually editable costs).
   * Displayed read-only in Cost Summary. Recalculated when any insufficient item's cost changes.
   */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number, default: 0 })
  subAssemblyCost: number

  /**
   * Stored insufficient items computed at creation via computeBomAsGreedy + summarizeBom.
   * Cost is manually editable. Refreshable via RefreshWorkOrderInsufficientItems action.
   */
  @Field(() => [WorkOrderInsufficientItem])
  @Prop({ type: [WorkOrderInsufficientItem] }, PropType.ARRAY)
  insufficientItems: ReturnType<() => WorkOrderInsufficientItem[]>

  /**
   * Total manufacturing cost = miscellaneousCost + totalOperationsCost + subAssemblyCost.
   * Recalculated when any cost component changes.
   */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number, default: 0 })
  totalManufactureCost: number

  @Field(() => [Task])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  tasks: ReturnType<() => Task[]>

  @Field(() => [Activity])
  @Prop({ type: [Activity] })
  remarksActivity: Activity[]

  @Field(() => [Asset])
  @Prop({ type: [Asset] })
  assets: Asset[]

  @Field(() => [InventoryBySerialNumber])
  @Prop({ type: () => [InventoryBySerialNumber] }, PropType.ARRAY)
  inventoryBySerialNumbers: ReturnType<() => InventoryBySerialNumber[]>

  /**
   * The following fields have fieldResolvers, we just need it here for type-safety
   */
  @Field(() => Float)
  progressNow: number

  @Field(() => Float)
  progressMax: number

  @Field(() => [Project])
  projects: Project[]

  @Field(() => Product)
  outOfStockProduct: ReturnType<() => Product>

  /**
   * Computed via FieldResolver — true if any items[].workOrder is populated.
   * Not stored in DB.
   */
  @Field(() => Boolean, { nullable: true })
  hasChildren?: boolean
}
